import { sameSuggestions } from '@/hooks/selectEqual';
import { useIdentifyStatus } from '@/hooks/useIdentifyQueue';
import { useRowboat, useSelect } from '@/rowboat';
import type { SuggestionRow } from '@/schema/estate';
import { getIdentifyQueue, type IdentifyQueue, type PauseReason } from '@/services/identifyQueue';
import {
  acceptSuggestion,
  rejectSuggestion,
  type SuggestionField,
  suggestionsForItem,
} from '@/services/suggestionService';

const actionButton =
  'min-h-9 rounded-full border border-divider bg-surface-primary px-3 py-1.5 text-sm font-medium text-content-secondary hover:bg-interactive-hover';

const iconButton =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-divider bg-surface-primary text-content-secondary hover:bg-interactive-hover';

const FIELD_LABEL: Record<SuggestionField, string> = {
  name: 'Name',
  description: 'Description',
  materials: 'Materials',
  dimensions: 'Dimensions',
  condition: 'Condition',
  category: 'Category',
  tag: 'Tags',
};

function Choice({ s }: { s: SuggestionRow }) {
  const graph = useRowboat();
  return (
    <>
      <button
        type="button"
        className={iconButton}
        aria-label={`Accept ${s.value}`}
        onClick={() => void acceptSuggestion(graph, s.id)}
      >
        ✓
      </button>
      <button
        type="button"
        className={iconButton}
        aria-label={`Reject ${s.value}`}
        onClick={() => void rejectSuggestion(graph, s.id)}
      >
        ✕
      </button>
    </>
  );
}

/**
 * Identification's writes into an empty field show only in the field itself. A Viewer sees no
 * pending suggestions: they are an editor's to accept or reject.
 */
export function FieldSuggestions({
  itemId,
  field,
  readOnly = false,
}: {
  itemId: string;
  field: SuggestionField;
  readOnly?: boolean;
}) {
  const graph = useRowboat();
  const rows = useSelect(
    () =>
      suggestionsForItem(graph, itemId).filter((s) => s.field === field && s.state === 'pending'),
    sameSuggestions,
  );
  if (rows.length === 0 || readOnly) return null;
  const label = (
    <span className="shrink-0 font-medium text-content-primary">{FIELD_LABEL[field]}</span>
  );
  if (field === 'tag') {
    return (
      <div className="flex items-start gap-2 text-sm text-content-secondary">
        <span className="py-1">{label}</span>
        <ul aria-label="Suggested tag" className="flex flex-wrap gap-1.5">
          {rows.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-1 rounded-full border border-divider py-0.5 pr-0.5 pl-2"
            >
              <span>{s.value}</span>
              <Choice s={s} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <ul aria-label={`Suggested ${field}`} className="space-y-1 text-sm text-content-secondary">
      {rows.map((s) => (
        <li key={s.id} className="flex items-start gap-2">
          <span className="py-1">{label}</span>
          <span className="min-w-0 flex-1 py-1 break-words">{s.value}</span>
          <Choice s={s} />
        </li>
      ))}
    </ul>
  );
}

const PAUSED_MESSAGE: Record<PauseReason, string> = {
  offline: "Identification paused: offline. It resumes when you're back online.",
  signed_out: 'Identification paused: signed out. It resumes when you sign in.',
  rate_limited: 'Identification paused: too many requests. It retries automatically.',
  daily_limit: 'Identification paused: daily limit reached. It resumes tomorrow.',
  service_unavailable: 'Identification paused: service unavailable. It retries automatically.',
};

export function IdentifyStatus({
  itemId,
  readOnly = false,
  queue = getIdentifyQueue(),
}: {
  itemId: string;
  readOnly?: boolean;
  queue?: IdentifyQueue;
}) {
  const { pending, failed, paused } = useIdentifyStatus(queue);
  if (failed.includes(itemId)) {
    return (
      <output className="block text-sm text-content-secondary">
        Identification failed.
        {!readOnly && (
          <>
            {' '}
            <button type="button" className={actionButton} onClick={() => void queue.retry(itemId)}>
              Retry identification
            </button>
          </>
        )}
      </output>
    );
  }
  if (pending.includes(itemId)) {
    return (
      <output className="block text-sm text-content-secondary">
        {paused ? PAUSED_MESSAGE[paused] : 'Identifying…'}
      </output>
    );
  }
  return null;
}
